import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import React, { useCallback, useState } from "react";
import { fetchQuery } from "react-relay";
import { formatDateTime } from "@components/DateTimeDisplay";
import { IdDisplayField } from "@components/id-display-field";
import { FetchFunction, NodeExtractor, usePagedPagination } from "@hooks/use-paged-pagination";
import { useTranslation } from "@i18n/hooks";
import { selectDiscountCodeField_Query } from "@relay/selectDiscountCodeField_Query.graphql";
import { QUERY_FRAGMENT } from "./select-discount-code-field.graphql";
import {
	DiscountCode,
	type SelectDiscountCodeFieldProps,
} from "./select-discount-code-field.interface";
import { styles } from "./select-discount-code-field.styles";
import { DataTable } from "../../../components/data-table";
import { RelayEnvironment } from "../../../RelayEnvironment";

export const SelectDiscountCodeField = ({
	updateField,
	fieldValue,
	filterByDiscountTypeKind = null,
}: SelectDiscountCodeFieldProps) => {
	const { t } = useTranslation("common");
	const [selected, setSelected] = useState(fieldValue ?? null);

	const fetchDiscountCodes: FetchFunction<selectDiscountCodeField_Query> = useCallback(
		({ first, after, filter }) => {
			return fetchQuery<selectDiscountCodeField_Query>(
				RelayEnvironment,
				QUERY_FRAGMENT,
				{
					first,
					after,
					code: filter.code ?? null,
					filterByDiscountTypeKind,
				},
				{ fetchPolicy: "network-only" },
			).toPromise();
		},
		[],
	);

	const discountCodeExtractor: NodeExtractor<selectDiscountCodeField_Query, DiscountCode> =
		useCallback((response) => {
			const connection = response?.Admin?.Billing?.searchDiscountCodes;

			if (!connection) {
				return { nodes: [], pageInfo: { hasNextPage: false, endCursor: null } };
			}

			const nodes = connection.edges?.map((e: any) => e.node) ?? [];

			return {
				nodes,
				pageInfo: connection.pageInfo,
			};
		}, []);

	const {
		currentItems,
		currentPage,
		loading,
		hasNext,
		filter,
		setFilter,
		handleNext,
		handlePrev,
		refetch,
	} = usePagedPagination<selectDiscountCodeField_Query, DiscountCode>({
		fetchFn: fetchDiscountCodes,
		nodeExtractor: discountCodeExtractor,
		initialFilter: { code: undefined },
	});

	const handleRowSelect = (row: DiscountCode) => {
		if (selected?.id === row.id) {
			setSelected(null);
			updateField(undefined);
		} else {
			setSelected(row);
			updateField(row);
		}
	};

	const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newFilter = { ...filter, code: e.target.value };
		setFilter(newFilter);
	};

	const handleClear = () => {
		setFilter({ code: undefined });
		refetch();
	};
	return (
		<div className={styles.mainContainer}>
			<div className={styles.filterRow}>
				<InputText
					placeholder={t("discountCode.code")}
					value={filter.code || ""}
					onChange={handleFilterChange}
				/>
				<Button type="reset" icon="pi pi-times" onClick={handleClear} />
			</div>

			{currentItems.length > 0 ? (
				<>
					<DataTable
						value={currentItems}
						loading={loading}
						stripedRows
						loadingTemplate={() => (
							<div className={styles.loadingTemplate}>
								<ProgressSpinner />
							</div>
						)}
					>
						{(Column) => [
							<Column
								key="id"
								header={t("table.id")}
								body={(node: DiscountCode) => (
									<div className={styles.idDisplayContainer}>
										<Checkbox
											checked={selected?.id === node.id}
											onChange={() => handleRowSelect(node)}
										/>
										<IdDisplayField id={node.id} />
									</div>
								)}
							/>,
							<Column
								key="code"
								header={t("discountCode.code")}
								body={(node: DiscountCode) => node.code}
							/>,
							<Column
								key="usages"
								header={t("discountCode.usages")}
								body={(node: DiscountCode) => node.numUsages}
							/>,
							<Column
								headerClassName={styles.createdAtContainer}
								className={styles.createdAtContainer}
								key="created"
								header={t("discountCode.createdAt")}
								body={(node: DiscountCode) => formatDateTime(node.createdAt)}
							/>,
						]}
					</DataTable>

					<div className={styles.paginationControllers}>
						<Button
							label={t("discountCode.previous")}
							onClick={handlePrev}
							type="button"
							disabled={currentPage === 0}
						/>
						<Button
							label={t("discountCode.next")}
							type="button"
							onClick={handleNext}
							disabled={!hasNext}
						/>
					</div>
				</>
			) : (
				<div className={styles.emptyState}>
					{!loading ? (
						t("discountCode.noDiscountCodesFound")
					) : (
						<div className={styles.loadingTemplate}>
							<ProgressSpinner />
						</div>
					)}
				</div>
			)}
		</div>
	);
};
